export const caseStudies = [
  {
    id: 1,
    slug: "emotions-clinic-first",
    client: "Emotions Clinic First",
    title: "CASE STUDY",
    subtitle: "Practice Management Transformation",
    category: "Healthcare & Mental Health",
    industry: "Healthcare / Mental Health & Well-being",
    duration: "2 Months",
    usersEnabled: "500+ Users",
    efficiencyGain: "100%",
    customerSatisfaction: "100%",
    image: "/images/case-study-emotions-clinic.jpg",
    pdfUrl: "/case-studies-pdf/emotions-clinic-first-case-study.pdf",
    logoType: "emotions-clinic",
    meta: {
      title: "Case Study: Emotions Clinic First | Practice Management Transformation | Tedekstra",
      description: "Discover how Tedekstra designed and implemented a tailored practice management platform for Emotions Clinic First.",
      keywords: ["Emotions Clinic First", "Healthcare Case Study", "Practice Management Platform", "Tedekstra"]
    },
    clientOverview: [
      "Emotions Clinic First is a growing healthcare practice providing specialist mental health and well-being services. With an expanding team of consultants and increasing patient volumes, the clinic required a modern and scalable solution to streamline patient management, financial operations, consultant payments, and administrative processes while maintaining high standards of patient care.",
      "As the business continued to grow, the leadership team recognised the need to modernise its systems and improve operational efficiency to support future expansion."
    ],
    challengeIntro: "The clinic was experiencing several operational challenges that were impacting efficiency, visibility, and scalability:",
    challenges: [
      "Patient information was being managed across multiple systems and manual processes.",
      "Difficulty maintaining a seamless patient journey from appointment booking through treatment and follow-up.",
      "Administrative teams spent significant time managing appointments, billing, and reporting activities.",
      "Reliance on spreadsheets and disconnected systems created data inconsistencies and operational inefficiencies.",
      "Manual invoice generation and tracking increased the risk of errors and delayed financial processes.",
      "The organisation required a secure and scalable solution capable of supporting future growth while maintaining compliance and data integrity.",
      "Limited visibility into consultant earnings, wage calculations, and overall financial performance."
    ],
    challengeOutro: "These challenges increased administrative overhead and reduced the time available for patient-focused activities.",
    solutionIntro: "Tedekstra partnered with Emotions Clinic First to design and implement a tailored and fully compliant practice management platform that centralised clinical, operational, and financial processes.",
    solutionItems: [
      "Centralised patient, consultant, and operational data management.",
      "Invoice generation, tracking, and management within a single platform.",
      "Appointment scheduling and patient workflow management.",
      "Financial reporting and operational performance visibility.",
      "Automated billing and payment processing.",
      "Automated administrative workflows and reporting processes.",
      "Consultant wage management and automated earnings calculations based on predefined business rules.",
      "Customised functionality aligned to the clinic's specific operational requirements."
    ],
    solutionOutro: "Our approach focused on improving operational efficiency, reducing administrative burden, and creating a scalable platform to support future growth.",
    implementationIntro: "The project was delivered using a phased methodology:",
    implementationPhases: [
      {
        phase: "Phase 1",
        title: "Discovery & Design",
        color: "pink",
        points: [
          "Stakeholder workshops and process reviews.",
          "Requirements gathering and workflow analysis.",
          "Solution design and system architecture."
        ]
      },
      {
        phase: "Phase 2",
        title: "Build & Configuration",
        color: "pink",
        points: [
          "Platform configuration and customisation.",
          "Patient, billing, and consultant management setup.",
          "Financial workflow and reporting configuration."
        ]
      },
      {
        phase: "Phase 3",
        title: "Testing & Training",
        color: "blue",
        points: [
          "User Acceptance Testing (UAT).",
          "Staff training sessions.",
          "Business process validation and refinements."
        ]
      },
      {
        phase: "Phase 4",
        title: "Go-Live & Support",
        color: "blue",
        points: [
          "Production deployment.",
          "User onboarding and support.",
          "Ongoing optimisation and enhancement planning."
        ]
      }
    ],
    outcomesIntro: "Following implementation, Emotions Clinic First achieved significant improvements across the organisation.",
    outcomesPillars: [
      {
        title: "Operational Efficiency",
        points: [
          "Reduced reliance on manual administrative processes.",
          "Streamlined patient, appointment, and operational management.",
          "Faster processing of billing, consultant payments, and invoicing activities.",
          "Reduced administrative workload across the organisation."
        ]
      },
      {
        title: "Financial Visibility & Control",
        points: [
          "Improved visibility into revenue, invoices, and consultant earnings.",
          "Automated consultant wage calculations improved payment accuracy.",
          "Centralised financial reporting and business performance monitoring.",
          "Enhanced confidence in financial management and reporting."
        ]
      },
      {
        title: "Patient Experience",
        points: [
          "More streamlined patient journey from appointment booking through treatment.",
          "Improved access to patient information across the organisation.",
          "Consistent and efficient service delivery."
        ]
      },
      {
        title: "Scalability",
        points: [
          "Centralised platform capable of supporting future clinic growth.",
          "Reduced dependence on spreadsheets and manual workarounds.",
          "Standardised business processes across clinical and administrative functions."
        ]
      }
    ],
    clientFeedback: {
      quote: "The team took the time to understand our requirements and delivered a solution that significantly improved our day-to-day operations. Their support throughout the project was excellent, and the system has helped us streamline processes while enhancing the overall experience for both our staff and patients.",
      attribution: "– Emotions Clinic First"
    },
    keyMetricsTable: [
      { metric: "Project Duration", result: "2 Months" },
      { metric: "Users Enabled", result: "500+ Users" },
      { metric: "Processes Automated", result: "100%" },
      { metric: "Operational Efficiency Gain", result: "100%" },
      { metric: "Customer Satisfaction", result: "100%" }
    ],
    lookingAhead: [
      "With a modern and scalable practice management platform in place, Emotions Clinic First is well-positioned to continue expanding its services while maintaining operational excellence.",
      "Tedekstra continues to support the clinic through ongoing optimisation, feature enhancements, and strategic technology guidance to ensure the platform evolves alongside the business."
    ]
  },
  {
    id: 2,
    slug: "pedal-power",
    client: "Pedal Power",
    title: "CASE STUDY",
    subtitle: "Digital Transformation of Cycle Hire and Fleet Management Operations",
    category: "Non-Profit & Community Fleet",
    industry: "Non-Profit / Community / Fleet & Recreation",
    duration: "1 Month",
    usersEnabled: "100+ Users",
    efficiencyGain: "100%",
    customerSatisfaction: "100%",
    image: "/images/case-study-pedal-power.jpg",
    pdfUrl: "/case-studies-pdf/pedal-power-case-study.pdf",
    logoType: "pedal-power",
    meta: {
      title: "Case Study: Pedal Power | Digital Transformation of Cycle Hire & Fleet Management | Tedekstra",
      description: "Learn how Tedekstra designed and implemented a bespoke digital platform that centralised customer bookings, fleet management, and operational reporting for Pedal Power.",
      keywords: ["Pedal Power", "Cycle Hire Case Study", "Fleet Management Software", "Tedekstra"]
    },
    clientOverview: [
      "Pedal Power is a charitable organisation based at Alyn Waters Country Park, providing inclusive cycle hire services for people of all abilities. Supporting individuals, families, schools, support workers, and larger community groups, the charity promotes health, wellbeing, and accessibility through cycling.",
      "As demand for its services continued to grow, Pedal Power recognised the need to modernise its digital presence and streamline the management of bookings, fleet operations, customer information, and reporting to support future growth and improve service delivery."
    ],
    challengeIntro: "The organisation faced several operational challenges that were impacting efficiency and scalability:",
    challenges: [
      "Reliance on manual processes for managing cycle bookings and customer information.",
      "Difficulty tracking bike maintenance and service history.",
      "A need for a scalable system that could support growing customer demand while improving the overall user experience.",
      "Lack of automated booking reminders and customer communications.",
      "Manual payment processes and limited invoicing capabilities.",
      "Limited visibility into ride statistics and bike utilisation.",
      "No integrated online booking platform to allow customers to reserve bikes and time slots.",
      "Challenges managing spare parts and inventory for the hire fleet.",
      "Paper-based disclaimers and consent forms creating administrative overhead."
    ],
    challengeOutro: "These challenges increased administrative workload and limited the charity's ability to focus on delivering accessible cycling experiences to the community.",
    solutionIntro: "Tedekstra partnered with Pedal Power to design and implement a bespoke digital platform that centralised customer bookings, fleet management, and operational reporting.",
    solutionItems: [
      "A modern website and landing page showcasing the charity and its services.",
      "Digital disclaimers with electronic signatures and acceptance of terms and conditions.",
      "Back-office functionality to manage bookings and create manual reservations.",
      "Data capture and reporting for riders, bike usage, families, schools, support workers, and group activities.",
      "An online booking system allowing customers to select bikes, dates, and time slots.",
      "A customer portal for self-service booking management.",
      "Fleet management capabilities including bike service records and maintenance scheduling.",
      "Invoice generation and operational reporting.",
      "Integration with social media feeds and newsletter updates.",
      "Automated booking confirmations and reminders.",
      "Access to customer details and emergency contact information when required.",
      "Spare parts and inventory management for the hire fleet.",
      "Secure payment processing through Stripe.",
      "Ride count tracking to trigger maintenance alerts after predefined usage thresholds."
    ],
    solutionOutro: "Our approach focused on improving operational efficiency while delivering an intuitive experience for both customers and staff.",
    implementationIntro: "The project was delivered using a phased methodology:",
    implementationPhases: [
      {
        phase: "Phase 1",
        title: "Discovery & Design",
        color: "pink",
        points: [
          "Stakeholder workshops and requirements gathering.",
          "User journey mapping for customers and administrators.",
          "Solution architecture and system design."
        ]
      },
      {
        phase: "Phase 2",
        title: "Build & Configuration",
        color: "pink",
        points: [
          "Website and booking platform development.",
          "Customer portal and digital disclaimer configuration.",
          "Fleet management and inventory functionality setup.",
          "Payment gateway and reporting configuration."
        ]
      },
      {
        phase: "Phase 3",
        title: "Testing & Training",
        color: "blue",
        points: [
          "User Acceptance Testing (UAT).",
          "Staff training sessions.",
          "Workflow validation and process refinement."
        ]
      },
      {
        phase: "Phase 4",
        title: "Go-Live & Support",
        color: "blue",
        points: [
          "Production deployment.",
          "Hypercare support and user onboarding.",
          "Ongoing enhancements and continuous improvement planning."
        ]
      }
    ],
    outcomesIntro: "Following implementation, Pedal Power achieved significant improvements across its operations.",
    outcomesPillars: [
      {
        title: "Operational Efficiency",
        points: [
          "Reduced manual administration associated with bookings and customer records.",
          "Streamlined booking and payment processes.",
          "Automated customer reminders and digital consent management.",
          "Improved day-to-day management of cycle hire operations."
        ]
      },
      {
        title: "Data Visibility & Reporting",
        points: [
          "Centralised reporting on ride activity and bike usage.",
          "Improved visibility into customer demographics and group participation.",
          "Ability to generate reports between selected dates.",
          "Better insights into fleet utilisation and operational performance."
        ]
      },
      {
        title: "Customer Experience",
        points: [
          "Convenient online self-service booking capabilities.",
          "Faster and simpler registration and disclaimer completion.",
          "Improved communication through automated reminders and confirmations.",
          "Enhanced customer experience for individuals, families, schools, and support groups."
        ]
      },
      {
        title: "Fleet Management & Scalability",
        points: [
          "Proactive maintenance scheduling based on ride counts.",
          "Complete service history tracking for each bike.",
          "Improved spare parts inventory management.",
          "A scalable platform capable of supporting future growth and increasing customer demand.",
          "Reduced dependence on spreadsheets and manual processes."
        ]
      }
    ],
    clientFeedback: {
      quote: "Tedekstra understood the unique requirements of our charity and delivered a platform that has transformed the way we manage bookings and our fleet. The new system has reduced administration, improved customer experience, and provided us with valuable insights into how our services are being used. Their support throughout the project has been exceptional.",
      attribution: "– Pedal Power"
    },
    keyMetricsTable: [
      { metric: "Project Duration", result: "1 Months" },
      { metric: "Users Enabled", result: "100+" },
      { metric: "Fleet Managed", result: "10 Fleet Categories" },
      { metric: "Processes Automated", result: "Booking Management, Digital Disclaimers, Fleet Maintenance, Reporting, Payments" },
      { metric: "Operational Efficiency Gain", result: "100%" },
      { metric: "Customer Satisfaction", result: "100%" }
    ],
    lookingAhead: [
      "With a modern and scalable digital platform in place, Pedal Power is well-positioned to continue expanding its services and supporting more individuals, families, schools, and community groups.",
      "Tedekstra continues to support the organisation through ongoing optimisation, platform enhancements, and strategic technology guidance, enabling the charity to focus on its mission of making cycling accessible for everyone."
    ]
  },
  {
    id: 3,
    slug: "godfrey-group",
    client: "Godfrey Group",
    title: "CASE STUDY",
    subtitle: "Transforming Cleaning & Housekeeping Operations with a Centralised Digital Management Platform",
    category: "Commercial Cleaning & Housekeeping",
    industry: "Commercial Cleaning / Housekeeping & Facilities Management",
    duration: "6 Months",
    usersEnabled: "5 User Roles",
    efficiencyGain: "100%",
    customerSatisfaction: "100%",
    image: "/images/case-study-godfrey-group.jpg",
    pdfUrl: "/case-studies-pdf/godfrey-group-case-study.pdf",
    logoType: "godfrey-group",
    meta: {
      title: "Case Study: Godfrey Group | Transforming Cleaning & Housekeeping Operations | Tedekstra",
      description: "Learn how Tedekstra designed and implemented a centralised digital cleaning and housekeeping management platform with QR-code-driven workflows for Godfrey Group.",
      keywords: ["Godfrey Group", "Cleaning Operations Case Study", "Housekeeping Management Platform", "QR Code Cleaning Management", "Tedekstra"]
    },
    clientOverview: [
      "Godfrey Group is a UK-based commercial cleaning and housekeeping services provider delivering cleaning solutions across holiday parks, hotels, offices, retail environments, education facilities, and industrial and construction sites.",
      "The organisation focuses on efficiency, reliability and professionalism, with technology playing an important role in electronically tracking and reporting service delivery. Godfrey Group's services include specialised housekeeping for holiday parks and hotels, commercial cleaning, facilities management and building maintenance."
    ],
    challengeIntro: "Managing cleaning operations across multiple locations, properties and individual accommodation units can involve a significant amount of coordination between cleaners, checkers, housekeeping managers, site managers and administrators. Godfrey Group required a more structured and centralised approach to managing the cleaning lifecycle, particularly for holiday park and accommodation sites. The key challenges included:",
    challenges: [
      "Managing cleaning requirements across multiple sites and individual lodges or properties.",
      "Allowing checkers to verify the condition of a lodge before it became available for guests.",
      "Providing management teams with real-time visibility of cleaning progress and property status.",
      "Providing cleaners with a simple and efficient way to confirm that cleaning had been completed.",
      "Identifying and reporting maintenance issues during cleaning and inspection.",
      "Reducing dependency on manual updates and communication between operational teams.",
      "Maintaining a historical record of previous cleaning activities and reports.",
      "Ensuring site managers were immediately informed when properties were ready for new guests.",
      "Producing regular operational reports and summaries for management."
    ],
    challengeOutro: "These challenges created administrative friction, delayed property turnaround times, and highlighted the need for a unified digital platform.",
    solutionIntro: "To address these requirements, a centralised digital cleaning and housekeeping management platform was designed and implemented. The solution introduced a QR-code-driven workflow, allowing each site, lodge, housing association property, room or office to have its own unique digital identity.",
    solutionItems: [
      "QR Code-Based Cleaning Management: Unique QR codes assigned to individual properties and assets for instant workflow access.",
      "Cleaner Workflow: Simplified mobile interface to confirm cleaning completion, report maintenance issues, and attach photographic evidence.",
      "Holiday Park Inspection & Verification: Structured verification process allowing checkers to verify lodge cleanliness and suitability before releasing to guests.",
      "Real-Time Property Status: Visual map and dashboard with colour-coded indicators (Awaiting cleaning, In progress, Clean & completed, Requiring attention, Not suitable for guests).",
      "Automated Notifications: Instant desktop notifications sent to site managers when properties are verified and ready for new guests.",
      "Cleaning History & Audit Trail: Complete historical record of cleans, inspections, reports, and photos for every property.",
      "Role-Based Dashboards: Dedicated visibility for Super Admins, Admins, Site Managers, Housekeeping Managers, and Operational Staff.",
      "Automated Daily Reporting: Professionally formatted daily summary PDF reports automatically compiled and emailed to site managers at the end of each day."
    ],
    solutionOutro: "The focus throughout the project was on creating a practical solution for on-site teams while providing management with greater control, visibility, and accountability across cleaning operations.",
    implementationIntro: "The project was delivered using a phased methodology:",
    implementationPhases: [
      {
        phase: "Phase 1",
        title: "Discovery & Process Mapping",
        color: "pink",
        points: [
          "Stakeholder workshops and on-site housekeeping workflow reviews.",
          "Role-based operational requirements gathering across holiday park and commercial sites.",
          "Solution architecture and QR code mapping design."
        ]
      },
      {
        phase: "Phase 2",
        title: "QR Workflow & Mobile Build",
        color: "pink",
        points: [
          "QR code generation architecture and unique property mapping setup.",
          "Simplified mobile cleaner interface with photographic evidence capture.",
          "Structured checker inspection and verification portal."
        ]
      },
      {
        phase: "Phase 3",
        title: "Dashboards & Automation",
        color: "blue",
        points: [
          "Colour-coded live status mapping dashboard for management teams.",
          "Automated daily PDF/Excel operational reporting engine.",
          "Desktop notification system for instant guest-ready alerts."
        ]
      },
      {
        phase: "Phase 4",
        title: "Testing, Rollout & Go-Live",
        color: "blue",
        points: [
          "User Acceptance Testing (UAT) with on-site cleaning and checking teams.",
          "Role-based training for cleaners, checkers, and site managers.",
          "Full production deployment and ongoing enhancement support."
        ]
      }
    ],
    outcomesIntro: "Following implementation, Godfrey Group achieved significant improvements across all cleaning and housekeeping operations.",
    outcomesPillars: [
      {
        title: "Operational Visibility",
        points: [
          "Management teams can see cleaning progress and property status from a centralised dashboard.",
          "Real-time visibility into property readiness across multiple holiday parks and locations.",
          "Eliminates guesswork and manual status chasing between teams."
        ]
      },
      {
        title: "Simplified Workflows & Quality Control",
        points: [
          "QR codes provide cleaners and checkers with instant access to property workflows.",
          "Structured verification ensures properties are checked before being released to guests.",
          "Centralised maintenance reporting with direct photo uploads."
        ]
      },
      {
        title: "Faster Communication & Automation",
        points: [
          "Automated notifications help site managers identify when properties are ready for guests.",
          "Automated daily PDF/Excel reports eliminate end-of-day manual administration.",
          "Reduces dependency on phone calls and manual status updates."
        ]
      },
      {
        title: "Historical Audit Trail & Scalability",
        points: [
          "Every cleaning activity creates a permanent digital audit trail.",
          "New sites and locations can be seamlessly configured as the business grows.",
          "Management receives a clear, consistent view of cleaning performance across locations."
        ]
      }
    ],
    clientFeedback: {
      quote: "The digital platform has helped us bring greater structure and visibility to our cleaning and housekeeping operations. The QR-code workflow makes it easier for our teams to record cleaning activity and report issues, while the dashboards, notifications and reporting give management a much clearer view of property status and operational progress. The system has provided us with a more consistent and transparent way of managing cleaning across our sites and has helped connect our on-site teams with our management processes.",
      attribution: "– Godfrey Group"
    },
    keyMetricsTable: [
      { metric: "Project Duration", result: "6 Months (June 2025 – Dec 2025)" },
      { metric: "Users Enabled", result: "5 User Roles (Admin, Manager, Staff, Checker, Customer)" },
      { metric: "Fleet Managed", result: "0 Fleet Categories" },
      { metric: "Processes Automated", result: "Task Management, Document Management, Automated PDF/Excel Reporting, Attendance & Geolocation Tracking, Payroll & Timesheet Management" },
      { metric: "Operational Efficiency Gain", result: "100%" },
      { metric: "Customer Satisfaction", result: "100%" }
    ],
    lookingAhead: [
      "With a unified digital cleaning and housekeeping management platform in place, Godfrey Group is primed to scale across additional holiday parks, commercial facilities, and residential portfolios.",
      "Tedekstra continues to collaborate closely with Godfrey Group on feature expansions, system optimisations, and enhanced analytics to support their ongoing growth and commitment to operational excellence."
    ]
  },
  {
    id: 4,
    slug: "touch-point",
    client: "Touch Point",
    title: "CASE STUDY",
    subtitle: "Enhancing an Existing Support Management Platform for Better Performance, Accessibility & Operational Efficiency",
    category: "Support Management & Integrations",
    industry: "Higher Education / Support Services & Student Finance",
    duration: "Ongoing Partnership",
    usersEnabled: "Multi-Organisation",
    efficiencyGain: "100%",
    customerSatisfaction: "100%",
    image: "/images/case-study-touch-point.jpg",
    pdfUrl: "/case-studies-pdf/touch-point-case-study.pdf",
    logoType: "touch-point",
    meta: {
      title: "Case Study: Touch Point | Support Management & SFE API Integration | Tedekstra",
      description: "Learn how Tedekstra partnered with Access Support to enhance the Touch Point platform with mobile responsiveness, direct home-screen access, and automated SFE API integration management.",
      keywords: ["Touch Point", "Access Support", "Student Finance England SFE API", "Support Management Platform", "Tedekstra"]
    },
    clientOverview: [
      "Access Support is a digital platform used by Touch Point to manage and administer their support services. The platform supports a range of operational activities and integrations, including organisation management, support administration and electronic invoicing through Student Finance England (SFE).",
      "Rather than replacing the existing platform, our engagement focused on enhancing and continuously improving the existing Touch Point solution to make it more responsive, accessible and easier to manage operationally."
    ],
    challengeIntro: "Touch Point was already an established platform being used for day-to-day business operations. However, as usage continued, there were areas where the existing experience could be improved:",
    challenges: [
      "The responsiveness of the web application on mobile devices was limited, making it difficult for users accessing the platform on phones.",
      "Lack of convenient mobile access for support workers and administrators working away from desktop computers.",
      "Operational dependencies within the platform's Student Finance England (SFE) API integration process.",
      "Enabling the live SFE API for a new organisation required technical team intervention after UAT completion.",
      "Need to reduce unnecessary technical dependencies in everyday operational workflows.",
      "Need for a dedicated capability to enable SFE API integrations without creating launch delays.",
      "Requirement for an agile technology partner for ongoing product enhancements and reliable technical support.",
      "Need to continuously evolve the platform based on changing business and regulatory requirements."
    ],
    challengeOutro: "Access Support required a technology partner to modernise mobile accessibility, automate integration workflows, and provide continuous platform evolution without disrupting live operations.",
    solutionIntro: "Our team worked alongside Access Support as an ongoing technology partner, focusing on targeted improvements to the existing Touch Point platform rather than rebuilding the product.",
    solutionItems: [
      "Improving Web Application Responsiveness: Optimised layouts and interfaces to work smoothly and consistently across mobile, tablet, and desktop devices.",
      "Direct Mobile Access Experience: Implemented a home-screen shortcut mechanism providing users with instant, app-like access to the login experience.",
      "Improving SFE API Management: Built direct self-service functionality allowing authorised users to manage organisation SFE API configurations, supplier codes, and keys.",
      "Interim SFE API Enablement: Delivered a dedicated interim capability to enable live SFE APIs without technical bottlenecks while broader tools were developed.",
      "Automated Invoicing Workflows: Streamlined electronic invoice generation, validation, and submission directly to Student Finance England.",
      "Ongoing Technical Support: Continuous issue resolution, operational support, integration maintenance, and feature enhancements.",
      "Collaborative Roadmap Planning: Working closely with Access Support to prioritize features that maximize operational impact.",
      "Platform Architecture Evolution: Maintained and strengthened platform stability, security, and scalability as organisation adoption grew."
    ],
    solutionOutro: "Our approach preserved the existing investment in Touch Point while significantly boosting user convenience, administrative autonomy, and system responsiveness.",
    implementationIntro: "The engagement has covered four key continuous improvement areas:",
    implementationPhases: [
      {
        phase: "Phase 1",
        title: "Mobile Responsiveness Audit",
        color: "pink",
        points: [
          "Full audit of mobile viewpoints and interface usability.",
          "Refactoring layouts for seamless performance across varying screen sizes.",
          "Cross-device validation and responsive navigation streamlining."
        ]
      },
      {
        phase: "Phase 2",
        title: "Direct Mobile Access",
        color: "pink",
        points: [
          "Home-screen shortcut configuration for direct app-like launcher access.",
          "Optimised mobile authentication and frictionless entry points.",
          "Mobile-first workflow testing and staff adoption validation."
        ]
      },
      {
        phase: "Phase 3",
        title: "SFE API Self-Service",
        color: "blue",
        points: [
          "In-platform SFE API configuration and supplier key management.",
          "Interim live API enablement capability to eliminate onboarding delays.",
          "Automated UAT invoice verification and end-to-end integration workflows."
        ]
      },
      {
        phase: "Phase 4",
        title: "Continuous Support & Evolution",
        color: "blue",
        points: [
          "Proactive issue investigation and rapid technical resolution.",
          "Sprint-based feature enhancements aligned with changing business needs.",
          "Ongoing roadmap collaboration and managed tech support."
        ]
      }
    ],
    outcomesIntro: "The continuous improvement approach has delivered several practical benefits for Access Support and Touch Point.",
    outcomesPillars: [
      {
        title: "Better Mobile Experience & Faster Access",
        points: [
          "Improvements to web application responsiveness make Touch Point practical across different devices.",
          "Direct home-screen shortcut provides simple access without repeatedly navigating a mobile browser.",
          "Smoother, more intuitive experience for staff working in the field."
        ]
      },
      {
        title: "Reduced Technical Dependency",
        points: [
          "Improvements to SFE API enablement reduce the need for technical intervention when organisations go live.",
          "Operational teams can handle configuration and integration management directly.",
          "Faster time-to-production for new partner organisations."
        ]
      },
      {
        title: "Continuity of Existing Platform",
        points: [
          "Targeted improvements made directly to the existing platform protected existing investment.",
          "Avoided costly, high-risk system replacement while continuously enhancing capabilities.",
          "Zero disruption to day-to-day operations during rollout."
        ]
      },
      {
        title: "Ongoing Product Evolution",
        points: [
          "Regular technical support and enhancements ensure the platform responds to new operational needs.",
          "Collaborative prioritization creates a living product that evolves with user feedback.",
          "Long-term engineering reliability and peace of mind."
        ]
      }
    ],
    clientFeedback: {
      quote: "The team has been an important technology partner in helping us continuously improve Touch Point. Rather than simply maintaining the platform, they have taken the time to understand our day-to-day challenges and identify practical improvements. The work on the responsiveness of the platform and easier mobile access has significantly improved the user experience, while the improvements around SFE API management have helped streamline our operational processes. Their ongoing support gives us confidence that we have a technical team we can rely on as our requirements continue to evolve.",
      attribution: "– Access Support / Touch Point"
    },
    keyMetricsTable: [
      { metric: "Project Duration", result: "Ongoing Technology Partnership" },
      { metric: "Users Enabled", result: "Multi-Organisation Platform" },
      { metric: "Processes Automated", result: "SFE API Enablement, Mobile Home-Screen Access, Electronic Invoicing, Support Administration" },
      { metric: "Operational Efficiency Gain", result: "100%" },
      { metric: "Customer Satisfaction", result: "100%" }
    ],
    lookingAhead: [
      "With a responsive web interface and autonomous SFE API management workflows, Touch Point is well-equipped to support an expanding network of higher education support providers and partner organisations.",
      "Tedekstra continues to act as the core technology partner for Access Support, delivering regular updates, performance enhancements, and future-ready integrations."
    ]
  },

  // ============================================================
  // 5. MPLOYR
  // ============================================================
  {
    id: 5,
    slug: "mployr",
    client: "Mployr",
    title: "CASE STUDY",
    subtitle:
      "Building a Digital Recruitment Platform & Driving Growth Through Digital Marketing",
    category: "Recruitment & Digital Growth",
    industry: "UK Home-Care Recruitment",
    duration: "Ongoing Partnership",
    usersEnabled: "Care Providers & Care Workers",
    efficiencyGain: "Digital Growth",
    customerSatisfaction: "Ongoing Partnership",

    image: "/images/case-study-mployr-2.png",
    pdfUrl: "/case-studies-pdf/mployr-clinic-first-case-study.pdf",
    logoType: "mployr",

    meta: {
      title:
        "Case Study: Mployr | Digital Recruitment Platform & Digital Marketing | Tedekstra",
      description:
        "Discover how Tedekstra partnered with Mployr to build a digital recruitment platform and support growth through website development, social media, paid advertising, email marketing, content marketing and digital strategy.",
      keywords: [
        "Mployr",
        "Digital Recruitment Platform",
        "Care Recruitment",
        "Digital Marketing",
        "UK Home-Care Recruitment",
        "Tedekstra",
      ],
    },

    // ============================================================
    // ABOUT MPLOYR
    // ============================================================
    clientOverview: [
      "Mployr is a digital recruitment platform focused on connecting UK home-care providers with care workers, helping care organisations find suitable candidates more directly and efficiently.",

      "Mployr's proposition is built around making recruitment faster, simpler and more direct, helping care providers reduce reliance on traditional recruitment channels while giving candidates easier access to relevant opportunities.",

      "Tedekstra partnered with Mployr not only to build and develop its digital presence, but also to support the business's wider growth through an ongoing digital marketing strategy.",

      "Our engagement therefore combined technology, website development and marketing to help Mployr establish its online presence, communicate its proposition effectively and reach both sides of its marketplace.",
    ],

    // ============================================================
    // BUSINESS CHALLENGE
    // ============================================================
    challengeIntro:
      "The UK care sector faces significant recruitment challenges, with care providers competing for a limited pool of skilled workers. Mployr identified an opportunity to create a more direct and efficient way for care providers and care workers to connect, reducing some of the delays and costs associated with traditional recruitment models. However, building the platform was only one part of the challenge. Mployr also needed to establish a strong digital presence and create awareness of the platform among its target audiences. The business therefore needed a technology and marketing partner who could:",

    challenges: [
      "Build a professional and user-friendly website for the Mployr platform.",
      "Clearly communicate the value proposition to both employers and candidates.",
      "Develop email marketing activity to maintain engagement and support lead generation.",
      "Establish Mployr's online brand presence.",
      "Continuously improve the digital presence based on business and market requirements.",
      "Use social media to build engagement and credibility.",
      "Create a digital experience that supports both sides of the recruitment marketplace.",
      "Create relevant content around recruitment, care workers and the wider home-care sector.",
      "Generate awareness within the competitive UK care recruitment market.",
      "Run targeted advertising campaigns to reach potential customers and users.",
    ],

    challengeOutro:
      "Mployr's challenge was therefore not simply to build a website, but to create and promote a digital ecosystem capable of supporting its growth.",

    // ============================================================
    // SOLUTION
    // ============================================================
    solutionIntro:
      "Tedekstra worked with Mployr across two connected areas:",

    solutionItems: [
      "1. Website & Digital Platform Development",
      "2. Ongoing Digital Marketing & Growth",

      "The approach was designed to ensure that the technology and marketing strategy worked together rather than operating as separate activities.",
    ],

    solutionOutro:
      "The combined technology and marketing approach created a digital ecosystem designed to establish Mployr's online presence, communicate its proposition effectively and support ongoing growth.",

    // ============================================================
    // IMPLEMENTATION / APPROACH
    // ============================================================
    implementationIntro:
      "The engagement combined website development and ongoing digital marketing across the following areas:",

    implementationPhases: [
      {
        phase: "Phase 1",
        title: "Building the Mployr Website & Digital Experience",
        color: "pink",
        points: [
          "Creating a professional digital presence for the Mployr brand.",
          "Clearly communicating the platform's purpose and value proposition.",
          "Designing an intuitive user journey.",
          "Providing clear pathways for care providers and care workers.",
          "Making the website responsive across devices.",
          "Creating a scalable digital foundation for future growth.",
          "Supporting the recruitment marketplace through a simple and accessible online experience.",
        ],
      },

      {
        phase: "Phase 2",
        title: "Building Awareness Through Social Media Marketing",
        color: "pink",
        points: [
          "Ongoing content creation and digital engagement.",
          "Creating content relevant to both care providers and care workers.",
          "Positioning Mployr as a knowledgeable voice within the UK care recruitment sector.",
          "Care recruitment trends.",
          "Challenges facing home-care providers.",
          "Advice for care workers.",
          "Recruitment and retention strategies.",
          "Workforce challenges.",
          "Hiring best practices.",
          "Industry insights.",
          "Employer branding.",
          "Career opportunities within the care sector.",
          "Educational and awareness-led content.",
        ],
      },

      {
        phase: "Phase 3",
        title: "Targeted Paid Advertising Campaigns",
        color: "blue",
        points: [
          "Increasing awareness of the Mployr platform.",
          "Reaching care providers with recruitment requirements.",
          "Reaching care workers actively looking for opportunities.",
          "Promoting relevant jobs and recruitment opportunities.",
          "Driving traffic towards the Mployr website.",
          "Supporting lead generation.",
          "Testing different messaging and audience segments.",
          "Optimising campaigns based on performance.",
        ],
      },

      {
        phase: "Phase 4",
        title: "Email Marketing",
        color: "blue",
        points: [
          "Maintaining relationships with audiences.",
          "Keeping users informed about relevant opportunities, platform developments and industry content.",
          "User engagement.",
          "Lead nurturing.",
          "Recruitment campaigns.",
          "Employer communication.",
          "Candidate communication.",
          "Platform awareness.",
          "Promotional campaigns.",
          "Re-engagement of existing contacts.",
        ],
      },

      {
        phase: "Phase 5",
        title: "Content & Thought Leadership",
        color: "pink",
        points: [
          "Positioning Mployr as more than simply a recruitment platform.",
          "Creating content that addresses staffing, retention, recruitment costs and workforce availability.",
          "Helping care providers attract and hire staff more effectively.",
          "Exploring how organisations can create environments where care workers want to stay.",
          "Understanding the wider recruitment pressures facing the UK care sector.",
          "Highlighting opportunities and benefits within the care profession.",
          "Exploring how technology can make recruitment faster and more efficient.",
        ],
      },
    ],

    // ============================================================
    // INTEGRATED DIGITAL GROWTH STRATEGY
    // ============================================================
    integratedDigitalGrowthStrategy: {
      title: "Integrated Digital Growth Strategy",

      intro:
        "The key strength of the engagement has been the combination of technology and marketing. Rather than treating the website as a standalone project, we have continued to support Mployr's wider digital journey.",

      services: [
        {
          title: "Website Development",
          description: "A professional and scalable digital platform.",
        },
        {
          title: "Social Media Marketing",
          description: "Consistent content and audience engagement.",
        },
        {
          title: "Paid Advertising",
          description:
            "Targeted campaigns designed to increase reach and generate relevant traffic.",
        },
        {
          title: "Email Marketing",
          description: "Ongoing communication and audience engagement.",
        },
        {
          title: "Content Marketing",
          description:
            "Industry-focused content designed to build authority and awareness.",
        },
        {
          title: "Digital Strategy",
          description:
            "Continuous alignment between technology, marketing and business objectives.",
        },
      ],
    },

    // ============================================================
    // OUTCOMES & BENEFITS
    // ============================================================
    outcomesIntro:
      "The combined technology and marketing approach has provided Mployr with a stronger foundation for digital growth.",

    outcomesPillars: [
      {
        title: "Professional Digital Presence",
        points: [
          "A dedicated website and digital platform provides Mployr with a professional online presence aligned with its recruitment proposition.",
        ],
      },

      {
        title: "Clearer Market Positioning",
        points: [
          "The website and marketing activity clearly communicate Mployr's role in connecting care providers with care workers.",
        ],
      },

      {
        title: "Increased Brand Awareness",
        points: [
          "Ongoing social media and paid advertising activity helps introduce Mployr to new audiences within the UK care sector.",
        ],
      },

      {
        title: "Targeted Audience Reach",
        points: [
          "Digital campaigns allow Mployr to target specific groups, including care providers and care workers.",
        ],
      },

      {
        title: "Consistent Brand Communication",
        points: [
          "Regular social media and email activity ensures that Mployr maintains an active and recognisable presence across its digital channels.",
        ],
      },

      {
        title: "Thought Leadership",
        points: [
          "Industry-focused content allows Mployr to participate in conversations around recruitment, retention and the future of the UK care workforce.",
        ],
      },

      {
        title: "Scalable Digital Foundation",
        points: [
          "The website provides a foundation that can continue to evolve as Mployr's user base, services and marketing requirements grow.",
        ],
      },
    ],

    // ============================================================
    // PARTNERSHIP
    // ============================================================
    partnership: {
      title: "Our Partnership With Mployr",

      paragraphs: [
        "Our relationship with Mployr demonstrates how technology development and digital marketing can work together to support business growth.",

        "We work alongside the Mployr team to understand its business objectives, target audiences and position within the competitive care recruitment market.",

        "Our role has extended beyond building the website.",

        "From developing the website and digital experience to creating social media content, running advertising campaigns and supporting email marketing, our focus has remained on helping Mployr establish and grow its digital presence.",

        "The ongoing partnership allows us to continuously adapt the digital and marketing strategy as Mployr evolves.",
      ],
    },

    // ============================================================
    // TESTIMONIAL
    // ============================================================
    clientFeedback: {
      quote:
        "Tedekstra went beyond our initial requirements and provided valuable IT and business consultancy throughout the project. They helped us develop a platform that is well positioned to meet the evolving needs of the UK home-care sector in a dynamic and competitive healthcare environment.",

      attribution: "— Jason McAteer [Co-Founder, Mployr]",
    },

    // ============================================================
    // KEY METRICS
    // ============================================================
    keyMetricsTable: [
      {
        metric: "Engagement",
        result: "Ongoing Technology & Digital Marketing Partnership",
      },
      {
        metric: "Digital Platform",
        result: "Professional and Scalable Recruitment Platform",
      },
      {
        metric: "Marketing Channels",
        result:
          "Website Development, Social Media, Paid Advertising, Email Marketing, Content Marketing & Digital Strategy",
      },
      {
        metric: "Target Audience",
        result: "UK Care Providers & Care Workers",
      },
    ],

    // ============================================================
    // THE IMPACT
    // ============================================================
    impact: {
      title: "The Impact",

      intro:
        "Mployr represents a partnership that goes beyond website development.",

      paragraphs: [
        "By combining a dedicated recruitment platform with an ongoing digital marketing strategy, we have helped create a digital ecosystem designed to support Mployr's ambitions in the UK care recruitment market.",

        "The result is a continuously developing digital presence that supports Mployr's objective of connecting care providers with care workers more efficiently.",

        "As the care sector continues to face recruitment and workforce challenges, Mployr provides a digital approach designed to make the connection between employers and candidates faster, simpler and more direct.",
      ],

      workBringsTogether:
        "Technology + Marketing + Content + Advertising + Digital Strategy",
    },

    // ============================================================
    // LOOKING AHEAD
    // ============================================================
    lookingAhead: [
      "With a professional and scalable digital platform supported by an ongoing marketing strategy, Mployr has a continuously developing digital presence designed to support its ambitions in the UK care recruitment market.",

      "Tedekstra continues to work alongside the Mployr team, adapting the digital and marketing strategy as the business, market and audience requirements evolve.",
    ],

    // ============================================================
    // ABOUT TEDEKSTRA
    // ============================================================
    aboutTedekstra: [
      "Tedekstra helps ambitious organisations transform operations through bespoke software solutions, digital transformation, ERP implementations, system integrations, and managed technology services.",

      "Bespoke Solutions.",

      "Enterprise Capability.",

      "No Upfront Development Cost.",
    ],
  },
  {
    id: 6,
    slug: "nail-tech-rich",
    client: "Nail Tech Rich",
    title: "CASE STUDY",
    subtitle: "A Digital Platform Built for Nail Professionals to Manage, Create & Grow",
    tagline: "Pricing, Booking & Marketing",
    category: "Beauty & Wellness Platform",
    industry: "Beauty & Wellness / Digital Platform & SaaS",
    duration: "Ongoing",
    usersEnabled: "1000+ Professionals",
    efficiencyGain: "100%",
    customerSatisfaction: "100%",
    image: "/images/nail_tech_rich_case_study_compact.jpg",
    pdfUrl: "/case-studies-pdf/nail-tech-rich-case-study.pdf",
    logoType: "nail-tech-rich",
    meta: {
      title: "Case Study: Nail Tech Rich | Digital Platform for Nail Professionals | Tedekstra",
      description: "Discover how Tedekstra engineered Nail Tech Rich, a specialised digital platform bringing client management, bookings, content creation, financial planning, personal development, and learning into one connected experience.",
      keywords: ["Nail Tech Rich", "Beauty Tech Case Study", "Nail Professional Platform", "Tedekstra", "Digital Transformation", "Bespoke Software"]
    },

    // Page 1: Project Overview
    clientOverview: [
      "Nail Tech Rich is a specialised digital platform designed to support nail professionals across the different aspects of managing and growing their business.",
      "Rather than focusing on a single business function, the platform brings client management, bookings, content creation, financial planning, personal development, services, and learning together within one connected experience."
    ],

    // Page 1: The Challenge
    challengeIntro: "Running a nail business involves much more than delivering nail services.",
    challenges: [
      "Professionals need to manage their clients and bookings, maintain their service offering, create engaging content, understand their income, and continue developing their skills.",
      "At the same time, personal development and mindset can play an important role in building and growing a business.",
      "The challenge was to bring these different needs together into one dedicated digital platform, rather than requiring users to rely on separate tools and experiences.",
      "The platform needed to balance the practical requirements of running a business with content focused on personal development, creativity, and continued learning."
    ],
    challengeOutro: "The challenge was to bring these diverse needs together into one dedicated digital platform, rather than requiring users to rely on separate tools and experiences.",

    // Page 1: Our Approach
    ourApproach: "At Tedekstra, our approach was centred around creating a platform that reflects the complete journey of a nail professional — from managing clients and services to developing their business and themselves.",

    // Page 2: Experience Structure (5 Key Areas)
    experienceStructureIntro: "We structured the experience around several key areas:",
    keyAreas: [
      {
        badge: "Manage",
        title: "Manage",
        description: "Keep client and booking information organised."
      },
      {
        badge: "Create",
        title: "Create",
        description: "Support content creation through an integrated caption-generation experience."
      },
      {
        badge: "Reflect",
        title: "Reflect",
        description: "Provide dedicated spaces for manifesting and journaling."
      },
      {
        badge: "Understand",
        title: "Understand",
        description: "Give users a dedicated income-calculation tool."
      },
      {
        badge: "Learn",
        title: "Learn",
        description: "Provide structured lesson-based content for ongoing development."
      }
    ],
    keyAreasOutro: "This approach allowed the platform to bring multiple requirements together while keeping each function within a clearly defined experience.",

    // Page 2: The Solution
    solutionIntro: "We designed Nail Tech Rich as an all-in-one platform combining business tools and personal-development experiences.",
    solutionBody: "Users can manage client and booking information, access client-specific notes, generate captions for nail-related content, explore manifesting content with audio, maintain journal entries, calculate income, view their service menu, and access monthly lessons.",
    solutionOutro: "The platform therefore extends beyond traditional booking or business-management software to create a more holistic experience for nail professionals.",
    solutionItems: [
      "Manage client and booking information in a centralised, easy-to-navigate interface.",
      "Access client-specific notes and relevant client details.",
      "Generate captions for nail-related social media content with an integrated Caption Generator.",
      "Explore manifesting content with associated audio directly within the platform.",
      "Maintain and record personal journaling entries in a dedicated reflection space.",
      "Calculate income and project financial earnings with an integrated Income Calculator.",
      "Organise and present full service menus with pricing and offerings.",
      "Access monthly lesson-based learning content (~20 lessons per month across Full and Business lessons)."
    ],

    // Page 3 & 4: Detailed Key Features
    detailedFeatures: [
      {
        id: "client-booking",
        badge: "Client & Booking Management",
        intro: "A dedicated client and booking experience allows users to manage essential information associated with their clients and appointments.",
        subheading: "Users can access:",
        bullets: [
          "Booking timings",
          "Client contact information",
          "Client-specific notes",
          "Relevant client details"
        ],
        outro: "This provides a centralised space for important client information."
      },
      {
        id: "caption-generator",
        badge: "Caption Generator",
        intro: "Creating social media content can be time-consuming. Nail Tech Rich incorporates a Caption Generator to support users when creating content around their nail designs.",
        bullets: [
          "Users can provide relevant information about their design and use the feature to generate a suitable caption."
        ]
      },
      {
        id: "manifesting-audio",
        badge: "Manifesting & Audio",
        intro: "The platform includes a dedicated Manifesting section combining written content with an audio experience.",
        bullets: [
          "Users can access the relevant manifesting content and listen to the associated audio directly within the platform."
        ]
      },
      {
        id: "journaling",
        badge: "Journaling",
        intro: "Personal reflection is incorporated through a dedicated Journal section.",
        bullets: [
          "Users can record and maintain their journal information within the application, giving personal-development activities their own dedicated space."
        ]
      },
      {
        id: "income-calculator",
        badge: "Income Calculator",
        intro: "The Income Calculator provides users with a dedicated financial tool within the platform.",
        bullets: [
          "Users can enter relevant income information and calculate their income, bringing a basic financial-planning capability directly into their business environment."
        ]
      },
      {
        id: "service-menu",
        badge: "Service Menu",
        intro: "A dedicated Service Menu allows users to view the services they offer.",
        bullets: [
          "This gives nail professionals a central place to organise and present their service offering within the platform."
        ]
      },
      {
        id: "learning-hub",
        badge: "Learning Hub",
        intro: "Learning forms an important part of the Nail Tech Rich experience. The platform incorporates a lesson-based structure with approximately 20 lessons per month, including the discussed categories of:",
        subheading: "Lesson Categories:",
        bullets: [
          "Full Lessons",
          "Business Lessons"
        ],
        outro: "This creates space for users to continue developing both their technical and business knowledge."
      }
    ],

    // Page 4: One Platform. Multiple Needs. (Consolidation Matrix)
    consolidationMatrix: {
      title: "One Platform. Multiple Needs.",
      intro: "Nail Tech Rich brings together several areas of a nail professional’s journey:",
      columns: [
        {
          name: "Business",
          items: ["Client Management", "Bookings", "Service Menu", "Income Calculator"]
        },
        {
          name: "Creativity",
          items: ["Caption Generator", "Nail Content", "—", "—"]
        },
        {
          name: "Personal Growth",
          items: ["Manifesting", "Audio Content", "Journaling", "—"]
        },
        {
          name: "Learning",
          items: ["Full Lessons", "Business Lessons", "Monthly Content", "—"]
        }
      ],
      outro: "Rather than treating these as isolated requirements, the platform connects them within one digital experience."
    },

    // Page 4: Impact
    impactSection: {
      title: "Impact",
      lead: "The key value of Nail Tech Rich lies in consolidation.",
      body: "The platform brings multiple business and personal-development functions into a single application, creating a dedicated digital environment for nail professionals."
    },

    // Page 5: Combination of Core Value Pillars
    valueCombinationIntro: "It provides a combination of:",
    valueCombinationPillars: [
      {
        badge: "Business Support",
        description: "Client, booking, service and income functionality."
      },
      {
        badge: "Content Support",
        description: "Tools to assist with creating nail-related social content."
      },
      {
        badge: "Personal Development",
        description: "Manifesting, audio and journaling experiences."
      },
      {
        badge: "Continuous Learning",
        description: "Structured monthly lesson-based content."
      }
    ],
    valueCombinationOutro: "The project demonstrates how a specialised digital platform can go beyond traditional business management and address the broader professional journey of its users.",

    // Page 5: The Tedekstra Difference
    tedekstraDifference: {
      title: "The Tedekstra Difference",
      paragraphs: [
        "Nail Tech Rich required more than simply building individual features. The opportunity was to bring diverse requirements — from bookings and income calculations to manifesting, journaling and learning — into a coherent product experience.",
        "By structuring these needs into clearly defined areas, we created a platform concept that combines business functionality with personal and professional development."
      ]
    },

    // Key metrics table for hub preview
    keyMetricsTable: [
      { metric: "Core Platform Functions", result: "7 Key Operational & Mindset Modules" },
      { metric: "Lessons per Month", result: "~20 Structured Lessons" },
      { metric: "Experience Scope", result: "Business + Creativity + Mindset + Learning" },
      { metric: "Approach", result: "Holistic All-in-One Consolidation" },
      { metric: "Development Model", result: "Bespoke SaaS / Zero Upfront Cost" }
    ],

    // About Tedekstra
    aboutTedekstra: [
      "Tedekstra helps ambitious organisations transform operations through bespoke software solutions, digital transformation, ERP implementations, system integrations, and managed technology services.",
      "Bespoke Solutions.",
      "Enterprise Capability.",
      "No Upfront Development Cost."
    ]
  }
];
